import React, { useState, useEffect } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import ServiceBreadcrumb from "../../layouts/components/ServiceBreadcrumb";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faInbox, faPaperPlane, faClipboardList, faHistory, faPlus, faCogs, faHandshake, faLayerGroup,
    faHeartbeat, faStethoscope, faExclamationTriangle, faCheckCircle, faBrain, faTooth, faBaby,
    faMicroscope, faDumbbell, faFemale, faEye, faTint, faAppleAlt, faUserMd, faProcedures,
    faAmbulance, faCalendarCheck, faFileMedical, faSearch, faUsers, faUserCheck, faComments,
    faShieldAlt, faGlobe, faBuilding, faFileContract, faHandshakeAlt
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import localForage from "localforage";

const ServiceRequests: React.FC = () => {
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [stats, setStats] = useState({
        generalRequests: 0,
        privateRequests: 0,
        receivedApplications: 0,
        sentApplications: 0,
        negotiations: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Service Requests - Uzima Healthcare";
        // Get clinic ID from localStorage
        localForage.getItem("clinic_id").then((clin: any) => {
            setClinicId(clin);
        });
    }, []);

    useEffect(() => {
        if (clinicId) {
            fetchStats();
        }
    }, [clinicId]);

    const fetchStats = async () => {
        if (!clinicId) return;
        
        try {
            setLoading(true);
            // Fetch statistics for each category using new endpoints
            const [generalRes, privateRes, receivedRes, sentRes, negotiationsRes] = await Promise.all([
                fetch(`${baseUrl}collaborations/general-requests/?count_only=true`),
                fetch(`${baseUrl}collaborations/private-requests/?count_only=true`),
                fetch(`${baseUrl}collaborations/applications/received/?count_only=true&clinic_id=${clinicId}`),
                fetch(`${baseUrl}collaborations/applications/sent/?count_only=true&clinic_id=${clinicId}`),
                fetch(`${baseUrl}collaborations/negotiations/?count_only=true&clinic_id=${clinicId}`)
            ]);

            const [generalData, privateData, receivedData, sentData, negotiationsData] = await Promise.all([
                generalRes.json(),
                privateRes.json(),
                receivedRes.json(),
                sentRes.json(),
                negotiationsRes.json()
            ]);

            setStats({
                generalRequests: generalData.count || 0,
                privateRequests: privateData.count || 0,
                receivedApplications: receivedData.count || 0,
                sentApplications: sentData.count || 0,
                negotiations: negotiationsData.count || 0
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
            // Set default values if API fails
            setStats({
                generalRequests: 0,
                privateRequests: 0,
                receivedApplications: 0,
                sentApplications: 0,
                negotiations: 0
            });
        } finally {
            setLoading(false);
        }
    };

    const getCardIcon = (category: string) => {
        switch (category) {
            case 'general': return faGlobe;
            case 'private': return faBuilding;
            case 'received': return faInbox;
            case 'sent': return faPaperPlane;
            case 'negotiations': return faHandshakeAlt;
            default: return faCogs;
        }
    };

    const getCardColor = (category: string) => {
        switch (category) {
            case 'general': return { bg: '#e3f2fd', color: '#1976d2', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
            case 'private': return { bg: '#fff3e0', color: '#f57c00', gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' };
            case 'received': return { bg: '#f3e5f5', color: '#7b1fa2', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)' };
            case 'sent': return { bg: '#e8f5e8', color: '#388e3c', gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' };
            case 'negotiations': return { bg: '#fff8e1', color: '#fbc02d', gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)' };
            default: return { bg: '#f5f5f5', color: '#757575', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
        }
    };

    const getCardDescription = (category: string) => {
        switch (category) {
            case 'general': return 'Service requests sent to all facilities offering the required service';
            case 'private': return 'Service requests sent directly to specific facilities';
            case 'received': return 'Applications received for your general service requests';
            case 'sent': return 'Service requests you have applied for (pending, rejected, or closed)';
            case 'negotiations': return 'Accepted service requests ready for collaboration setup';
            default: return 'Manage service request activities';
        }
    };

    const handleCardClick = (category: string) => {
        switch (category) {
            case 'general':
                navigate('/institution/services/general-requests');
                break;
            case 'private':
                navigate('/institution/services/private-requests');
                break;
            case 'received':
                navigate('/institution/services/received-applications');
                break;
            case 'sent':
                navigate('/institution/services/sent-applications');
                break;
            case 'negotiations':
                navigate('/institution/services/negotiations');
                break;
        }
    };

    const serviceCategories = [
        {
            id: 'general',
            title: 'General Requests',
            subtitle: 'Sent to all qualified providers',
            route: '/institution/services/service-requests/general'
        },
        {
            id: 'private',
            title: 'Private Requests',
            subtitle: 'Sent to specific facilities',
            route: '/institution/services/service-requests/private'
        },
        {
            id: 'received',
            title: 'Received Applications',
            subtitle: 'Applications for your requests',
            route: '/institution/services/service-requests/received-applications'
        },
        {
            id: 'sent',
            title: 'Sent Applications',
            subtitle: 'Your service applications',
            route: '/institution/services/service-requests/sent-applications'
        },
        {
            id: 'negotiations',
            title: 'Negotiations',
            subtitle: 'Ready for collaboration setup',
            route: '/institution/services/service-requests/negotiations'
        }
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        <ServiceBreadcrumb currentPage="Service Requests" />
                        <div className="row mb-4 align-items-center">
                            <div className="col-8">
                                <h2 className="fw-bold mb-0">
                                    <FontAwesomeIcon icon={faInbox} className="me-2 text-primary" />
                                    Service Requests
                                </h2>
                                <div className="text-muted">Manage all aspects of service requests and applications</div>
                            </div>
                            <div className="col-4 text-end">
                                <button 
                                    className="btn btn-primary btn-lg"
                                    onClick={() => navigate('/institution/services/create-request')}
                                >
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Create Service Request
                                </button>
                            </div>
                        </div>

                        {/* Statistics Overview */}
                        {!loading && (
                            <div className="row mb-4">
                                {Object.entries(stats).map(([key, value]) => {
                                    const category = key.replace(/([A-Z])/g, '_$1').toLowerCase();
                                    const colors = getCardColor(category);
                                    return (
                                        <div key={key} className="col-lg-2 col-md-4 col-6 mb-3">
                                            <div className="card border-0 shadow-sm text-center">
                                                <div className="card-body p-3">
                                                    <div 
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            background: colors.gradient,
                                                            color: 'white'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon 
                                                            icon={getCardIcon(category)} 
                                                            size="lg"
                                                        />
                                                    </div>
                                                    <h4 className="fw-bold text-dark mb-1">{value}</h4>
                                                    <small className="text-muted text-capitalize">
                                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Service Request Categories */}
                        <div className="row g-4">
                            {serviceCategories.map((category) => {
                                const colors = getCardColor(category.id);
                                const icon = getCardIcon(category.id);
                                const description = getCardDescription(category.id);
                                const count = stats[category.id as keyof typeof stats] || 0;
                                
                                return (
                                    <div key={category.id} className="col-lg-6 col-xl-4">
                                        <div 
                                            className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                                            style={{ 
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                            }}
                                            onClick={() => handleCardClick(category.id)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                            }}
                                        >
                                            {/* Count Badge */}
                                            <div className="position-absolute top-0 end-0 p-3">
                                                <span className="badge bg-primary fs-6 px-3 py-2">
                                                    {count}
                                                </span>
                                            </div>

                                            <div className="card-body p-4">
                                                {/* Icon and Title */}
                                                <div className="text-center mb-4">
                                                    <div 
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                        style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            background: colors.gradient,
                                                            color: 'white',
                                                            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon 
                                                            icon={icon} 
                                                            size="2x"
                                                        />
                                                    </div>
                                                    <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                                        {category.title}
                                                    </h5>
                                                    <p className="text-muted mb-0 small">
                                                        {category.subtitle}
                                                    </p>
                                                </div>

                                                {/* Description */}
                                                <p className="card-text text-muted mb-4" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                    {description}
                                                </p>

                                                {/* Action Button */}
                                                <div className="text-center">
                                                    <button 
                                                        className="btn btn-primary btn-sm px-4 py-2"
                                                        style={{
                                                            background: colors.gradient,
                                                            border: 'none',
                                                            borderRadius: '25px',
                                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faEye} className="me-2" />
                                                        View {category.title}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Decorative Elements */}
                                            <div 
                                                className="position-absolute bottom-0 start-0 w-100 h-100"
                                                style={{
                                                    background: 'linear-gradient(135deg, transparent 0%, rgba(102, 126, 234, 0.05) 100%)',
                                                    pointerEvents: 'none'
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Welcome Message */}
                        <div className="row mt-5">
                            <div className="col-12">
                                <div className="text-center">
                                    <h4 className="text-muted mb-3">Service Request Management Center</h4>
                                    <p className="text-muted">Click on any category above to manage different aspects of your service requests and applications.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    );
};

export default ServiceRequests;
